import { useCallback, useEffect, useRef } from 'react';
import { trackRumAction } from '@/shared/observability/rum';
import { ensureAnonSessionId } from './useSignupContext';
import { useUnauthAttribution, type UnauthAttribution } from './useUnauthAttribution';

/**
 * Wrapper around `trackRumAction` for the unauthenticated pre-login flow.
 *
 * Auto-attaches `anonSessionId` (spec linhas 146-150) and referral/invite
 * attribution properties (spec linhas 175-178) to every event so dashboards
 * can group the whole pre-signup session.
 *
 * Returned function signature mirrors `trackRumAction(name, properties)`.
 * Event names must match the spec table exactly (linhas 156-173) — dashboards
 * filter by literal strings.
 *
 * Implementation detail: attribution is held in a ref so the returned callback
 * has a stable identity for the lifetime of the component. Callers can include
 * it in `useEffect` dependency arrays without causing the effect to re-run
 * when attribution resolves asynchronously (which would otherwise emit
 * duplicate events for debounced inputs like the search box).
 */
export const useUnauthRumAction = () => {
  const { attribution } = useUnauthAttribution();
  const attributionRef = useRef<UnauthAttribution | null>(attribution);

  useEffect(() => {
    attributionRef.current = attribution;
  }, [attribution]);

  return useCallback((name: string, properties?: Record<string, unknown>) => {
    const anonSessionId = ensureAnonSessionId();
    const attr = attributionRef.current;
    trackRumAction(name, {
      anonSessionId,
      ...(attr?.referralCode && { referralCode: attr.referralCode }),
      ...(attr?.referralType && { referralType: attr.referralType }),
      ...(attr?.providerName && { providerName: attr.providerName }),
      ...(attr?.partnerCompanyName && {
        partnerCompanyName: attr.partnerCompanyName,
      }),
      ...(attr?.inviterRole && { inviterType: attr.inviterRole }),
      ...properties,
    });
  }, []);
};
